// Story 1.21 — SPA private page for the creator-application flow.
//
// Status-driven render:
//   * no application                 → application form
//   * pending                        → pending status card
//   * approved                       → redirect to /account/creator-dashboard
//                                       (placeholder; falls back to / if route absent)
//   * rejected + within 30d cooldown → cooldown card
//   * rejected + > 30d ago           → application form with "welcome back" hint

import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { CreatorApplicationForm } from "../features/creator-application/CreatorApplicationForm";
import { PendingApplicationCard } from "../features/creator-application/PendingApplicationCard";
import { RejectedCooldownCard } from "../features/creator-application/RejectedCooldownCard";
import { useCreatorApplicationStatus } from "../features/creator-application/useCreatorApplicationStatus";

export function BecomeCreatorPage() {
  const { data, isLoading, error } = useCreatorApplicationStatus();
  const navigate = useNavigate();

  useEffect(() => {
    if (data?.data?.status === "approved") {
      // The dashboard route isn't shipped yet; bounce to the home
      // page for now so the approved creator isn't left on a dead
      // surface.
      navigate("/", { replace: true });
    }
  }, [data, navigate]);

  if (isLoading) {
    return (
      <main className="min-h-screen flex items-center justify-center p-8">
        <p className="opacity-70">Loading…</p>
      </main>
    );
  }
  if (error) {
    return (
      <main className="min-h-screen flex items-center justify-center p-8">
        <p className="text-destructive">
          Couldn't load your application status. Try refreshing.
        </p>
      </main>
    );
  }

  const application = data?.data ?? null;

  let content;
  if (!application) {
    content = <CreatorApplicationForm />;
  } else if (application.status === "pending") {
    content = <PendingApplicationCard application={application} />;
  } else if (application.status === "rejected") {
    if (application.can_resubmit) {
      content = <CreatorApplicationForm showRejectedHint />;
    } else {
      content = <RejectedCooldownCard application={application} />;
    }
  } else if (application.status === "approved") {
    return <Navigate to="/" replace />;
  } else {
    // withdrawn — let them re-apply.
    content = <CreatorApplicationForm />;
  }

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
            Become a Greenhold creator
          </h1>
          <p className="opacity-80">
            Fill in your details below. The live certificate preview shows
            exactly how your hello text and image will appear on every share
            certificate of you.
          </p>
        </header>
        {content}
      </div>
    </main>
  );
}
